import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAxiosAuth from '../hooks/useAxiosAuth';
import { useDispatch } from 'react-redux';
import { forgotPassword, resetPassword, verifyingOtp, checkEmailExists } from '../redux/authSlice';
import { useTranslation } from 'react-i18next';
import { User, Lock, Eye, EyeOff, Stethoscope, Heart, ArrowLeftCircle, Languages, Check, AlertTriangle } from 'lucide-react';

// ... (other imports and code remain unchanged)

export default function ForgotPassword() {
    const { t } = useTranslation('forgotpassword');
    const { t: tLogin, i18n } = useTranslation('login');
    const navigate = useNavigate();
    const location = useLocation();
    const axiosInstance = useAxiosAuth();
    const language = localStorage.getItem('i18nextLng') || 'en';
    const dispatch = useDispatch();
    const languagePopupRef = useRef(null);
    const [step, setStep] = useState('email'); // 'email', 'otp', 'password'
    const [docId, setDocId] = useState(null); // To store doctor ID after OTP verification
    const [formData, setFormData] = useState({
        email: location.state?.email || '',
        newPassword: '',
        confirmPassword: '',
    });
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [errors, setErrors] = useState({
        email: '',
        newPassword: '',
        confirmPassword: '',
        otp: '',
    });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);
    const [isLanguagePopupOpen, setIsLanguagePopupOpen] = useState(false);
    const [isChecked, setIsChecked] = useState(i18n.language === 'fr');
    const [emailStatus, setEmailStatus] = useState(null); // null: not checked, true: exists, false: doesn't exist
    const [emailCheckLoading, setEmailCheckLoading] = useState(false);
    const emailCheckTimeoutRef = useRef(null);
    const lastCheckedEmailRef = useRef('');
    const isEmailCheckInProgressRef = useRef(false);

    // Email validation regex
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    // Validate email format
    const validateEmail = (email) => {
        if (!email) {
            return t('forgotpassword.email_required');
        }
        
        // Sanitize input - remove dangerous characters
        const sanitized = email.replace(/[<>]/g, '');
        
        // Check for whitespace-only
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('forgotpassword.email_required');
        }
        
        // Check max length (254 characters is standard email max length)
        if (sanitized.length > 254) {
            return t('forgotpassword.email_too_long') || 'Email cannot exceed 254 characters';
        }
        
        // Check local part (before @) max 64 characters
        const localPart = sanitized.split('@')[0];
        if (localPart && localPart.length > 64) {
            return t('forgotpassword.email_local_part_too_long');
        }
        
        // Check domain part (after @) max 253 characters
        const domainPart = sanitized.split('@')[1];
        if (domainPart && domainPart.length > 253) {
            return t('forgotpassword.email_domain_part_too_long');
        }
        
        // Check email format
        if (!emailRegex.test(sanitized)) {
            return t('forgotpassword.invalid_email_format');
        }
        
        return '';
    };
    
    // Validate password and get strength
    const validatePassword = (pwd) => {
        if (!pwd) {
            return t('forgotpassword.password_required');
        }
        
        // Sanitize input - remove dangerous characters
        const sanitized = pwd.replace(/[<>]/g, '');
        
        // Check for whitespace-only
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('forgotpassword.password_required');
        }
        
        // Check min length
        if (sanitized.length < 8) {
            return t('forgotpassword.password_too_short') || 'Password must be at least 8 characters long';
        }
        
        // Check max length
        if (sanitized.length > 25) {
            return t('forgotpassword.password_too_long');
        }
        
        // Check for at least one uppercase letter
        if (!/[A-Z]/.test(sanitized)) {
            return t('forgotpassword.password_uppercase_required') || 'Password must contain at least one uppercase letter';
        }
        
        // Check for at least one lowercase letter
        if (!/[a-z]/.test(sanitized)) {
            return t('forgotpassword.password_lowercase_required') || 'Password must contain at least one lowercase letter';
        }
        
        // Check for at least one number
        if (!/[0-9]/.test(sanitized)) {
            return t('forgotpassword.password_number_required') || 'Password must contain at least one number';
        }
        
        // Check for at least one special character
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(sanitized)) {
            return t('forgotpassword.password_special_char_required') || 'Password must contain at least one special character';
        }
        
        return '';
    };
    
    const getPasswordStrength = (pwd) => {
        const errors = validatePassword(pwd);
        if (errors) return 'Weak';
        return 'Strong';
    };

    const getPasswordStrengthLabel = (pwd) => {
        return getPasswordStrength(pwd) === 'Strong'
            ? t('forgotpassword.password_strength_strong')
            : t('forgotpassword.password_strength_weak');
    };

    // Debounced email check for forgot password (email must exist)
    useEffect(() => {
        if (emailCheckTimeoutRef.current) {
            clearTimeout(emailCheckTimeoutRef.current);
        }

        const email = formData.email?.trim() || '';

        // If email is empty or invalid format, reset status
        if (!email || !emailRegex.test(email)) {
            setEmailStatus(null);
            lastCheckedEmailRef.current = '';
            return;
        }

        // If email hasn't changed or check is in progress, don't check again
        if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
            return;
        }

        // Set new timeout for debounced check (500ms delay)
        emailCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedEmail = formData.email.trim();
            
            // Double check email is still valid and hasn't changed during debounce
            if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
                return;
            }

            // Mark check as in progress and update last checked email BEFORE making the API call
            isEmailCheckInProgressRef.current = true;
            lastCheckedEmailRef.current = trimmedEmail;
            setEmailCheckLoading(true);

            try {
                const resultAction = await dispatch(checkEmailExists({
                    email: trimmedEmail,
                    axiosInstance
                }));

                // Only update status if the email hasn't changed during the API call
                if (formData.email.trim() === trimmedEmail) {
                    if (checkEmailExists.fulfilled.match(resultAction)) {
                        // exists: true means email exists (good for forgot password)
                        // exists: false means email doesn't exist (bad for forgot password)
                        setEmailStatus(resultAction.payload.exists);
                        if (!resultAction.payload.exists) {
                            setErrors((prev) => ({ ...prev, email: t('forgotpassword.user_not_exist') }));
                        } else {
                            setErrors((prev) => ({ ...prev, email: '' }));
                        }
                    } else {
                        setEmailStatus(null);
                        lastCheckedEmailRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking email:', error);
                if (formData.email.trim() === trimmedEmail) {
                    setEmailStatus(null);
                    lastCheckedEmailRef.current = '';
                }
            } finally {
                isEmailCheckInProgressRef.current = false;
                setEmailCheckLoading(false);
            }
        }, 500);

        // Cleanup function
        return () => {
            if (emailCheckTimeoutRef.current) {
                clearTimeout(emailCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.email]);

    // Handle input changes with validation
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Sanitize input - remove dangerous characters
        let sanitized = value.replace(/[<>]/g, '');
        
        // Enforce max length for email
        if (name === 'email' && sanitized.length > 254) {
            sanitized = sanitized.substring(0, 254);
        }
        // Enforce max length for password
        if ((name === 'newPassword' || name === 'confirmPassword') && sanitized.length > 25) {
            sanitized = sanitized.substring(0, 25);
        }
        
        setFormData((prev) => ({ ...prev, [name]: sanitized }));

        if (name === 'email') {
            // Reset email status when email changes
            setEmailStatus(null);
            lastCheckedEmailRef.current = '';
            isEmailCheckInProgressRef.current = false;
            
            const error = validateEmail(sanitized);
            setErrors((prev) => ({ ...prev, email: error }));
        }

        if (name === 'newPassword') {
            const error = validatePassword(sanitized);
            setErrors((prev) => ({ ...prev, newPassword: error }));

            // Check confirm password match
            if (formData.confirmPassword && sanitized !== formData.confirmPassword) {
                setErrors((prev) => ({ ...prev, confirmPassword: t('forgotpassword.passwords_do_not_match') }));
            } else if (formData.confirmPassword) {
                setErrors((prev) => ({ ...prev, confirmPassword: '' }));
            }
        }

        if (name === 'confirmPassword') {
            if (sanitized !== formData.newPassword) {
                setErrors((prev) => ({ ...prev, confirmPassword: t('forgotpassword.passwords_do_not_match') }));
            } else {
                setErrors((prev) => ({ ...prev, confirmPassword: '' }));
            }
        }
    };

    // Handle OTP change
    const handleOtpChange = (index, value) => {
        const digit = value.slice(-1);
        if (!/^[0-9]?$/.test(digit)) return;

        const newOtp = [...otp];
        newOtp[index] = digit;
        setOtp(newOtp);

        if (digit && index < otp.length - 1) {
            document.getElementById(`otp-${index + 1}`)?.focus();
        }

        if (newOtp.every((d) => d !== '')) {
            setErrors((prev) => ({ ...prev, otp: '' }));
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (otp[index]) {
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
            } else if (index > 0) {
                document.getElementById(`otp-${index - 1}`)?.focus();
            }
        }
    };

    // Handle paste for OTP
    const handlePaste = (e) => {
        const paste = e.clipboardData.getData('text').trim();
        if (/^\d{6}$/.test(paste)) {
            setOtp(paste.split(''));
            setErrors((prev) => ({ ...prev, otp: '' }));
        }
    };

    // Handle send OTP
    const handleSendOtp = async (e) => {
        e.preventDefault();

        const emailError = validateEmail(formData.email);
        if (emailError) {
            setErrors((prev) => ({ ...prev, email: emailError }));
            toast.warning(t('forgotpassword.form_errors') || 'Please fix the errors in the form', { autoClose: 2000 });
            return;
        }

        if (!formData.email) {
            toast.warning(t('forgotpassword.email_required'), { autoClose: 2000 });
            return;
        }

        // Ensure email exists before sending OTP
        if (emailStatus !== true) {
            toast.warning(t('forgotpassword.user_not_exist'), { autoClose: 2000 });
            return;
        }

        setIsLoading(true);
        try {
            const result = await dispatch(forgotPassword({ email: formData.email, axiosInstance })).unwrap();
            toast.success(t('forgotpassword.otp_sent_success'), { autoClose: 2000 });
            setDocId(result.id);
            setStep('otp');
            setResendTimer(90);
        } catch (err) {
            const errorMessage = language === 'fr' ? err?.response?.data?.message?.fr : err?.response?.data?.message?.en;
            toast.warning(errorMessage || t('forgotpassword.otp_send_failed'), { autoClose: 2000 });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle OTP validation
    const handleValidateOtp = async (e) => {
        e.preventDefault();

        const otpCode = otp.join('');
        if (otpCode.length !== 6) {
            setErrors((prev) => ({ ...prev, otp: t('forgotpassword.invalid_otp') }));
            toast.warning(t('forgotpassword.invalid_otp'), { autoClose: 2000 });
            return;
        }

        setIsLoading(true);
        try {
            const result = await dispatch(verifyingOtp({ email: formData.email, otpCode, axiosInstance })).unwrap();
            toast.success(t('forgotpassword.otp_verified_success'), { autoClose: 2000 });
            setStep('password');
        } catch (error) {
            const errorMessage = language === 'fr' ? error?.response?.data?.message?.fr : error?.response?.data?.message?.en;
            setErrors((prev) => ({ ...prev, otp: errorMessage || t('forgotpassword.otp_error_invalid') }));
            toast.warning(errorMessage || t('forgotpassword.otp_error_invalid'), { autoClose: 2000 });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle resend OTP
    const handleResend = async () => {
        if (resendTimer > 0 || isLoading) return;

        setIsLoading(true);
        try {
            const result = await dispatch(forgotPassword({ email: formData.email, axiosInstance })).unwrap();
            setDocId(result.id);
            toast.success(t('forgotpassword.otp_resend_success'), { autoClose: 2000 });
            setResendTimer(90);
        } catch (err) {
            const errorMessage = language === 'fr' ? err?.response?.data?.message?.fr : err?.response?.data?.message?.en;
            toast.warning(errorMessage || t('forgotpassword.otp_resend_failed'), { autoClose: 2000 });
        } finally {
            setIsLoading(false);
        }
    };

    // Resend timer countdown
    useEffect(() => {
        if (resendTimer > 0) {
            const interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [resendTimer]);

    // Handle password reset
    const handleReset = async (e) => {
        e.preventDefault();

        const passwordError = validatePassword(formData.newPassword);
        const confirmPasswordError = formData.newPassword !== formData.confirmPassword 
            ? t('forgotpassword.passwords_do_not_match') 
            : '';
        
        if (passwordError || confirmPasswordError) {
            setErrors({ 
                newPassword: passwordError, 
                confirmPassword: confirmPasswordError 
            });
            toast.warning(t('forgotpassword.form_errors') || 'Please fix the errors in the form', { autoClose: 2000 });
            return;
        }

        if (!formData.newPassword || !formData.confirmPassword) {
            toast.warning(t('forgotpassword.form_errors'), { autoClose: 2000 });
            return;
        }

        if (!docId) {
            toast.warning(t('forgotpassword.invalid_session'), { autoClose: 2000 });
            setStep('email');
            return;
        }

        setIsLoading(true);
        try {
            await dispatch(
                resetPassword({
                    docId,
                    email: formData.email,
                    password: formData.newPassword,
                    confirmPassword: formData.confirmPassword,
                    axiosInstance,
                })
            ).unwrap();
            toast.success(t('forgotpassword.password_reset_success'), { autoClose: 2000 });
            setFormData({
                email: '',
                newPassword: '',
                confirmPassword: '',
            });
            setOtp(['', '', '', '', '', '']);
            setDocId(null);
            navigate('/login');
        } catch (err) {
            const errorMessage = language === 'fr' ? err?.response?.data?.message?.fr : err?.response?.data?.message?.en;
            toast.warning(errorMessage || t('forgotpassword.password_reset_failed'), { autoClose: 2000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        if (step === 'email') {
            navigate('/login');
        } else if (step === 'otp') {
            setStep('email');
            setOtp(['', '', '', '', '', '']);
        } else if (step === 'password') {
            setStep('otp');
            setFormData((prev) => ({
                ...prev,
                newPassword: '',
                confirmPassword: '',
            }));
        }
    };

    // Language switcher logic
    const handleToggleChange = () => {
        const newLanguage = isChecked ? 'en' : 'fr';
        i18n.changeLanguage(newLanguage);
        setIsChecked(!isChecked);
    };

    const handleLanguageChange = (language) => {
        i18n.changeLanguage(language);
        setIsLanguagePopupOpen(false);
        setIsChecked(language === 'fr');
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (languagePopupRef.current && !languagePopupRef.current.contains(event.target)) {
                setIsLanguagePopupOpen(false);
            }
        };
        if (isLanguagePopupOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isLanguagePopupOpen]);

    return (
        <div data-testid="forgot-password-page" className="min-h-screen relative w-full flex flex-col items-center justify-center px-2 sm:px-4 md:px-6 lg:px-8 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B]/90 overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-20 -right-20 sm:-top-40 sm:-right-40 w-40 h-40 sm:w-80 sm:h-80 bg-white/20 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob"></div>
                <div className="absolute -bottom-20 -left-20 sm:-bottom-40 sm:-left-40 w-40 h-40 sm:w-80 sm:h-80 bg-gray-200/30 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-blob animation-delay-2000"></div>
                <div className="absolute top-20 left-20 sm:top-40 sm:left-40 w-40 h-40 sm:w-80 sm:h-80 bg-white/10 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob animation-delay-4000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 sm:w-96 sm:h-96 bg-white/5 rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-blob animation-delay-1000"></div>
            </div>

            {/* Language Switcher */}
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50">
                <button
                    data-testid="language-toggle-button"
                    onClick={() => setIsLanguagePopupOpen(!isLanguagePopupOpen)}
                    className="flex items-center gap-2 p-1.5 text-white focus:outline-none cursor-pointer bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg rounded-2xl"
                    aria-label={t('forgotpassword.select_language')}
                >
                    <Languages className="h-6 w-6" />
                </button>
                {isLanguagePopupOpen && (
                    <div
                        data-testid="language-popup"
                        ref={languagePopupRef}
                        className="absolute right-0 top-full mt-2 w-32 bg-white rounded-lg shadow-lg p-2 z-40 border border-gray-200"
                    >
                        <button
                            data-testid="english-option"
                            onClick={() => handleLanguageChange('en')}
                            className={`w-full text-left mb-1 px-4 cursor-pointer py-2 text-sm rounded-md ${i18n.language === 'en'
                                ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            {tLogin('language_english')}
                        </button>
                        <button
                            data-testid="french-option"
                            onClick={() => handleLanguageChange('fr')}
                            className={`w-full text-left px-4 cursor-pointer py-2 text-sm rounded-md ${i18n.language === 'fr'
                                ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                                }`}
                        >
                            {tLogin('language_french')}
                        </button>
                    </div>
                )}
            </div>

            {/* Custom Styles */}
            <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes typewriter {
          from {
            width: 0;
          }
          to {
            width: 100%;
          }
        }
        @keyframes blink {
          0%, 50% {
            border-color: transparent;
          }
          51%, 100% {
            border-color: #4ABDBD;
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse 3s ease-in-out infinite;
        }
        .animate-slideInUp {
          animation: slideInUp 0.8s ease-out;
        }
        .animate-slideInLeft {
          animation: slideInLeft 0.8s ease-out;
        }
        .animate-slideInRight {
          animation: slideInRight 0.8s ease-out;
        }
        .animate-typewriter {
          animation: typewriter 2s steps(40, end);
        }
        .animate-blink {
          animation: blink 1s infinite;
        }
        .text-gradient {
          background: linear-gradient(135deg, #4ABDBD 0%, #3A9B9B 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glass-effect {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
      `}</style>

            {/* Main Container */}
            <div className="w-full max-w-7xl flex flex-col lg:flex-row items-center justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-12 relative z-10 py-4 sm:py-8">
                {/* Left Section - Branding */}
                <div className="flex flex-col items-center justify-center gap-4 sm:gap-6 md:gap-8 w-full lg:w-1/2 animate-slideInLeft px-4 sm:px-0">
                    {/* Animated Logo */}
                    <div className="relative">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-sm rounded-full flex items-center justify-center animate-float shadow-2xl border border-white/30">
                            <Stethoscope className="text-white text-2xl sm:text-3xl md:text-4xl animate-pulse-slow" />
                        </div>
                        <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-4 h-4 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-white/40 rounded-full animate-ping"></div>
                        <div className="absolute -bottom-1 -left-1 sm:-bottom-2 sm:-left-2 w-3 h-3 sm:w-4 sm:h-4 md:w-6 md:h-6 bg-white/30 rounded-full animate-ping animation-delay-2000"></div>
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 bg-white/5 rounded-full animate-pulse-slow"></div>
                    </div>

                    {/* Animated Title */}
                    <div className="text-center px-2">
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-4 drop-shadow-lg">
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.2s' }}>T</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.3s' }}>h</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.4s' }}>e</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.5s' }}>r</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.6s' }}>a</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.7s' }}>p</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.8s' }}>e</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.9s' }}>u</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.0s' }}> </span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.1s' }}>T</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.2s' }}>i</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.3s' }}>m</span>
                            <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.4s' }}>e</span>
                        </h1>
                        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 font-light animate-slideInUp drop-shadow-md" style={{ animationDelay: '1.6s' }}>
                            {t('forgotpassword.welcome_quote1')}
                        </p>
                    </div>

                    {/* Floating Icons */}
                    <div className="flex gap-4 sm:gap-6 md:gap-8 animate-slideInUp" style={{ animationDelay: '1.8s' }}>
                        <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center animate-float border border-white/30 shadow-lg">
                            <Stethoscope className="text-white text-lg sm:text-xl md:text-2xl" />
                        </div>
                        <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/15 backdrop-blur-sm rounded-full flex items-center justify-center animate-float border border-white/20 shadow-lg" style={{ animationDelay: '1s' }}>
                            <Heart className="text-white text-lg sm:text-xl md:text-2xl" />
                        </div>
                        <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center animate-float border border-white/25 shadow-lg" style={{ animationDelay: '2s' }}>
                            <Stethoscope className="text-white text-lg sm:text-xl md:text-2xl" />
                        </div>
                    </div>
                </div>

                {/* Right Section - Forgot Password Form */}
                <div data-testid="forgot-password-container" className="relative z-10 bg-white/95 backdrop-blur-md p-4 sm:p-6 md:p-8 lg:p-12 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-sm sm:max-w-md flex flex-col gap-4 sm:gap-6 md:gap-8 animate-slideInRight border border-white/20 mx-2 sm:mx-0">
                    {/* Header with Back Button */}
                    <div data-testid="forgot-password-header" className="flex items-center gap-4 animate-slideInUp" style={{ animationDelay: '0.5s' }}>
                        <button
                            data-testid="back-button"
                            onClick={handleBack}
                            disabled={isLoading}
                            className={`bg-[#4ABDBD] text-white w-fit h-fit p-2 sm:p-2.5 rounded-full hover:bg-[#3A9B9B] font-medium transition-all duration-300  shadow-lg hover:shadow-xl transform hover:scale-105 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            aria-label={t('forgotpassword.go_back')}
                        >
                            <ArrowLeftCircle data-testid="back-icon" className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                        <div className="flex justify-center items-center w-full">
                            <h2
                                data-testid="forgot-password-title"
                                className="text-lg sm:text-xl md:text-2xl font-bold text-center text-[#4ABDBD]"
                            >
                                {step === 'email'
                                    ? t('forgotpassword.generate_otp_title')
                                    : step === 'otp'
                                        ? t('forgotpassword.otp_verification_title')
                                        : t('forgotpassword.reset_password_title')}
                            </h2>
                        </div>
                    </div>

                    {step === 'email' ? (
                        <>
                            <p data-testid="email-instruction" className="text-center text-gray-700 text-sm sm:text-base md:text-lg font-medium italic animate-slideInUp" style={{ animationDelay: '0.5s' }}>
                                {t('forgotpassword.email_instruction')}
                            </p>
                            <form data-testid="forgot-password-form" onSubmit={handleSendOtp} className="w-full">
                                <div data-testid="email-input-container" className={`relative flex items-center border rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-4 py-2 sm:py-3 mb-3 sm:mb-4 bg-white/80 backdrop-blur-sm transition-all duration-300 shadow-sm ${
                                    emailStatus === true
                                        ? 'border-green-300 focus-within:ring-2 focus-within:ring-green-500 focus-within:bg-white focus-within:border-green-500'
                                        : emailStatus === false
                                        ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500 focus-within:bg-white focus-within:border-red-500'
                                        : errors.email
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:bg-white focus-within:border-[#4ABDBD]'
                                }`}>
                                    <User data-testid="email-icon" className="mr-2 sm:mr-2.5 md:mr-3 text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                                    <input
                                        data-testid="email-input"
                                        name="email"
                                        type="email"
                                        placeholder={t('forgotpassword.email_placeholder')}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        maxLength={254}
                                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-500 text-sm sm:text-sm md:text-base pr-8"
                                        required
                                        autoComplete="email"
                                        disabled={isLoading}
                                    />
                                    {emailCheckLoading && (
                                        <span className="absolute right-3 w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                    )}
                                    {!emailCheckLoading && emailStatus === true && (
                                        <Check className="absolute right-3 w-5 h-5 text-green-500" />
                                    )}
                                </div>
                                {errors.email && (
                                    <p data-testid="email-error" className="text-red-500 text-xs mb-3 -mt-1 animate-slideInUp">{errors.email}</p>
                                )}
                                <button
                                    data-testid="send-otp-button"
                                    type="submit"
                                    disabled={isLoading || errors.email || !formData.email || emailStatus !== true}
                                    className={`w-full py-2.5 sm:py-3 font-bold rounded-lg sm:rounded-xl text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] hover:from-[#4ABDBD]/90 hover:to-[#3A9B9B]/90 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105 ${isLoading || errors.email || !formData.email || emailStatus !== true ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    {isLoading ? (
                                        <span
                                            data-testid="loading-spinner"
                                            className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"
                                        ></span>
                                    ) : (
                                        <>
                                            <Stethoscope className="text-sm sm:text-base md:text-lg" />
                                            {t('forgotpassword.send_otp_button')}
                                        </>
                                    )}
                                </button>
                            </form>
                        </>
                    ) : step === 'otp' ? (
                        <>
                            <p data-testid="otp-instruction" className="text-center text-gray-700 text-sm sm:text-base md:text-lg font-medium italic animate-slideInUp" style={{ animationDelay: '0.5s' }}>
                                {t('forgotpassword.otp_instruction', { email: formData.email })}
                            </p>
                            <form data-testid="otp-form" onSubmit={handleValidateOtp} className="w-full">
                                <div data-testid="otp-inputs-container" className="flex justify-center gap-2 sm:gap-3 mb-3 sm:mb-4" onPaste={handlePaste}>
                                    {otp.map((digit, index) => (
                                        <input
                                            key={index}
                                            id={`otp-${index}`}
                                            data-testid={`otp-input-${index}`}
                                            type="text"
                                            maxLength={1}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                            className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-center text-lg sm:text-xl md:text-2xl border border-gray-200 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] bg-white/80 backdrop-blur-sm transition-all duration-300 shadow-sm"
                                            autoComplete="off"
                                            disabled={isLoading}
                                        />
                                    ))}
                                </div>
                                {errors.otp && (
                                    <p className="text-red-300 text-xs mb-3 -mt-1 text-center animate-slideInUp">{errors.otp}</p>
                                )}
                                <p className="text-center text-xs sm:text-sm md:text-base mb-3 sm:mb-4 animate-slideInUp">
                                    {t('forgotpassword.didnt_receive_otp')}{' '}
                                    <span
                                        id="btn-resend-otp"
                                        onClick={handleResend}
                                        className={`text-[#4ABDBD] cursor-pointer hover:text-[#3A9B9B] hover:underline transition-colors ${resendTimer > 0 || isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {resendTimer > 0
                                            ? t('forgotpassword.resend_otp_with_timer', { seconds: resendTimer })
                                            : t('forgotpassword.resend_otp')}
                                    </span>
                                </p>
                                <button
                                    id="btn-verify-otp"
                                    type="submit"
                                    disabled={isLoading || otp.join('').length !== 6}
                                    className={`w-full py-2.5 sm:py-3 font-bold rounded-lg sm:rounded-xl text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] hover:from-[#4ABDBD]/90 hover:to-[#3A9B9B]/90 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105 ${isLoading || otp.join('').length !== 6 ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    {isLoading ? (
                                        <span
                                            id="loading-spinner"
                                            className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"
                                        ></span>
                                    ) : (
                                        <>
                                            <Stethoscope className="text-sm sm:text-base md:text-lg" />
                                            {t('forgotpassword.verify_otp_button')}
                                        </>
                                    )}
                                </button>
                            </form>
                        </>
                    ) : (
                        <>
                            <p className="text-center text-gray-700 text-sm sm:text-base md:text-lg font-medium italic animate-slideInUp" style={{ animationDelay: '0.5s' }}>
                                {t('forgotpassword.password_instruction', { email: formData.email })}
                            </p>
                            <form onSubmit={handleReset} className="w-full" id="reset-password-form">
                                <div className={`relative flex items-center border rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-4 py-2 sm:py-3 mb-3 sm:mb-4 ${errors.newPassword ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white/80 backdrop-blur-sm'} focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:bg-white focus-within:border-[#4ABDBD] transition-all duration-300 shadow-sm`}>
                                    <Lock className="mr-2 sm:mr-2.5 md:mr-3 text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                                    <input
                                        id="input-new-password"
                                        name="newPassword"
                                        type={showNewPassword ? 'text' : 'password'}
                                        placeholder={t('forgotpassword.new_password_placeholder')}
                                        value={formData.newPassword}
                                        onChange={handleInputChange}
                                        maxLength={25}
                                        className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-500 text-sm sm:text-sm md:text-base`}
                                        required
                                        autoComplete="new-password"
                                        disabled={isLoading}
                                    />
                                    <button
                                        id="btn-toggle-new-password"
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="ml-2 sm:ml-2.5 md:ml-3 focus:outline-none hover:scale-105 transition-transform cursor-pointer"
                                        aria-label={showNewPassword ? t('forgotpassword.hide_password_aria') : t('forgotpassword.show_password_aria')}
                                        disabled={isLoading}
                                    >
                                        {showNewPassword ? (
                                            <Eye className="text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                                        ) : (
                                            <EyeOff className="text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                                        )}
                                    </button>
                                </div>
                                {errors.newPassword && (
                                    <p className="text-red-300 text-xs mb-3 -mt-1 animate-slideInUp">{errors.newPassword}</p>
                                )}
                                {formData.newPassword && !errors.newPassword && (
                                    <p className={`text-xs mt-1 flex items-center gap-1 ${getPasswordStrength(formData.newPassword) === 'Strong' ? 'text-green-600' : 'text-amber-600'}`}>
                                        {getPasswordStrength(formData.newPassword) === 'Strong' ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                                        {t('forgotpassword.password_strength_label')}: {getPasswordStrengthLabel(formData.newPassword)}
                                    </p>
                                )}
                                <div className={`relative flex items-center border rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-4 py-2 sm:py-3 mb-3 sm:mb-4 ${errors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white/80 backdrop-blur-sm'} focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:bg-white focus-within:border-[#4ABDBD] transition-all duration-300 shadow-sm`}>
                                    <Lock className="mr-2 sm:mr-2.5 md:mr-3 text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                                    <input
                                        id="input-confirm-password"
                                        name="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder={t('forgotpassword.confirm_password_placeholder')}
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        maxLength={25}
                                        className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-500 text-sm sm:text-sm md:text-base`}
                                        required
                                        autoComplete="new-password"
                                        disabled={isLoading}
                                    />
                                    <button
                                        id="btn-toggle-confirm-password"
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="ml-2 sm:ml-2.5 md:ml-3 focus:outline-none hover:scale-105 transition-transform cursor-pointer"
                                        aria-label={showConfirmPassword ? t('forgotpassword.hide_password_aria') : t('forgotpassword.show_password_aria')}
                                        disabled={isLoading}
                                    >
                                        {showConfirmPassword ? (
                                            <Eye className="text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                                        ) : (
                                            <EyeOff className="text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                                        )}
                                    </button>
                                </div>
                                {errors.confirmPassword && (
                                    <p className="text-red-300 text-xs mb-3 -mt-1 animate-slideInUp">{errors.confirmPassword}</p>
                                )}
                                <button
                                    id="btn-reset-password"
                                    type="submit"
                                    disabled={isLoading || errors.newPassword || errors.confirmPassword || !formData.newPassword || !formData.confirmPassword}
                                    className={`w-full py-2.5 sm:py-3 font-bold rounded-lg sm:rounded-xl text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] hover:from-[#4ABDBD]/90 hover:to-[#3A9B9B]/90 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105 ${isLoading || errors.newPassword || errors.confirmPassword || !formData.newPassword || !formData.confirmPassword ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    {isLoading ? (
                                        <span
                                            id="loading-spinner"
                                            className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"
                                        ></span>
                                    ) : (
                                        <>
                                            <Stethoscope className="text-sm sm:text-base md:text-lg" />
                                            {t('forgotpassword.reset_password_button')}
                                        </>
                                    )}
                                </button>
                            </form>
                        </>
                    )}

                    {/* Footer */}
                    <footer id="forgot-password-footer" className="text-center text-xs sm:text-sm text-gray-600 animate-slideInUp" style={{ animationDelay: '1.7s' }}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                            <span>
                                {t('forgotpassword.footer_copyright', { year: new Date().getFullYear() })}
                            </span>
                        </div>
                        <span className="text-[#4ABDBD] font-medium">
                            <span id="app-version" className="text-[#4ABDBD] font-medium mr-3">
                                {t('forgotpassword.app_version')}
                            </span>
                            {t('forgotpassword.powered_by')}
                        </span>
                    </footer>
                </div>
            </div>
        </div>
    );
}